import { useState, useEffect, useCallback } from 'react';

// This hook provides a wrapper around the WebAssembly pricing module
const useWasmPricing = () => {
  const [wasmModule, setWasmModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the WebAssembly module
  useEffect(() => {
    // Temporarily disable Wasm loading to prevent build errors
    // The import path '../../wasm/pkg' is outside the src/ directory
    // and causes a "Module not found" error with Create React App.
    // To re-enable, this path issue needs to be resolved (e.g., by moving
    // the wasm/pkg directory into public/ or configuring Webpack).
    console.warn('WebAssembly loading is temporarily disabled due to import path issues.');
    setIsLoading(false);
    setWasmModule(null); // Ensure wasmModule is null
    setError(new Error('Wasm loading disabled.')); // Set an error state
  }, []);

  // Calculate sandwich price using WebAssembly
  const calculateSandwichPrice = useCallback((sandwichConfig) => {
    if (!wasmModule) {
      // Return null to signal that WASM isn't available yet
      return null;
    }

    try {
      // Convert the config to a string to pass to Rust
      const configJson = JSON.stringify(sandwichConfig);
      return wasmModule.calculate_sandwich_price(configJson);
    } catch (err) {
      console.error('Error calculating sandwich price:', err);
      return null;
    }
  }, [wasmModule]);

  // Calculate combo price using WebAssembly
  const calculateComboPrice = useCallback((sandwichPrice, drinkId, sideId) => {
    if (!wasmModule) {
      return null;
    }

    try {
      return wasmModule.calculate_combo_price(sandwichPrice, drinkId, sideId);
    } catch (err) {
      console.error('Error calculating combo price:', err);
      return null;
    }
  }, [wasmModule]);

  // Calculate prices for multiple sandwiches at once
  const bulkPriceCalculation = useCallback((sandwichConfigs) => {
    if (!wasmModule) {
      return null;
    }

    try {
      const configsJson = JSON.stringify(sandwichConfigs);
      const result = wasmModule.bulk_price_calculation(configsJson);
      return JSON.parse(result);
    } catch (err) {
      console.error('Error calculating bulk prices:', err);
      return null;
    }
  }, [wasmModule]);

  return {
    calculateSandwichPrice,
    calculateComboPrice,
    bulkPriceCalculation,
    isLoading,
    isWasmAvailable: !!wasmModule,
    error
  };
};

export default useWasmPricing;